import React, { useState } from 'react';
import { useToast } from '../../contexts/ToastContext';
import './HeaderDemo.css';

/**
 * Header Demo Component
 * Demonstrates all header features and how to access them
 */
const HeaderDemo = () => {
  const { showSuccess, showInfo, showWarning } = useToast();
  const [demoStep, setDemoStep] = useState(0);

  const demoSteps = [
    {
      title: "🚀 Welcome to the New Header!",
      description: "Your new app-wide header provides quick access to all features.",
      action: "Let's explore what's new!"
    },
    {
      title: "🧭 Navigation Made Easy",
      description: "Use the navigation buttons in the header to quickly switch between sections.",
      action: "Try clicking Dashboard, Trading, Wallets, etc."
    },
    {
      title: "💼 Quick Wallet Access",
      description: "The wallet selector in the header lets you switch wallets instantly.",
      action: "Look for the wallet dropdown in the top-right"
    },
    {
      title: "⚡ Quick Actions Menu",
      description: "Click the lightning bolt (⚡) for instant access to common actions.",
      action: "Quick Trade, New Wallet, Price Alerts, and more!"
    },
    {
      title: "🔔 Smart Notifications",
      description: "The bell icon shows your notifications with unread count badges.",
      action: "Stay updated on trades, alerts, and system events"
    },
    {
      title: "👤 User Menu",
      description: "Click your avatar for profile, settings, and logout options.",
      action: "Access Settings, Profile, Help, and Logout"
    },
    {
      title: "🎨 Theme Toggle",
      description: "Switch between light and dark themes instantly.",
      action: "Perfect for day and night trading sessions"
    },
    {
      title: "📱 Mobile Optimized",
      description: "On mobile, you get a clean header plus bottom navigation.",
      action: "Resize your browser to see mobile view"
    }
  ];

  const currentStep = demoSteps[demoStep];

  const nextStep = () => {
    if (demoStep < demoSteps.length - 1) {
      setDemoStep(demoStep + 1);
      showInfo(`Step ${demoStep + 2}: ${demoSteps[demoStep + 1].title}`);
    } else {
      showSuccess("Demo completed! Enjoy your new header!");
      setDemoStep(0);
    }
  };

  const prevStep = () => {
    if (demoStep > 0) {
      setDemoStep(demoStep - 1);
    }
  };

  const features = [
    {
      icon: "🧭",
      title: "Smart Navigation",
      description: "Quick access to all app sections with active state indicators"
    },
    {
      icon: "💼",
      title: "Wallet Integration",
      description: "Seamless wallet switching with current wallet display"
    },
    {
      icon: "⚡",
      title: "Quick Actions",
      description: "One-click access to frequently used features"
    },
    {
      icon: "🔔",
      title: "Real-time Notifications",
      description: "Stay informed with smart notification system"
    },
    {
      icon: "👤",
      title: "User Management",
      description: "Profile, settings, and account controls"
    },
    {
      icon: "🎨",
      title: "Theme Control",
      description: "Instant theme switching for optimal viewing"
    },
    {
      icon: "📱",
      title: "Mobile Responsive",
      description: "Optimized experience across all devices"
    },
    {
      icon: "🔋",
      title: "Power Efficient",
      description: "Built with battery-saving optimizations"
    }
  ];

  return (
    <div className="header-demo">
      <div className="demo-header">
        <h1>🚀 New Header Features</h1>
        <p>Discover all the powerful features in your new app-wide header</p>
      </div>

      {/* Interactive Demo */}
      <div className="demo-walkthrough">
        <div className="demo-card">
          <div className="demo-step-indicator">
            <span className="step-number">{demoStep + 1}</span>
            <span className="step-total">/ {demoSteps.length}</span>
          </div>
          
          <div className="demo-content">
            <h3>{currentStep.title}</h3>
            <p>{currentStep.description}</p>
            <div className="demo-action">
              <strong>Try this:</strong> {currentStep.action}
            </div>
          </div>
          
          <div className="demo-controls">
            <button 
              className="demo-btn secondary"
              onClick={prevStep}
              disabled={demoStep === 0}
            >
              ← Previous
            </button>
            <button 
              className="demo-btn primary"
              onClick={nextStep}
            >
              {demoStep === demoSteps.length - 1 ? 'Restart Demo' : 'Next →'}
            </button>
          </div>
        </div>
      </div>

      {/* Feature Grid */}
      <div className="features-grid">
        <h2>🌟 Header Features Overview</h2>
        <div className="features-container">
          {features.map((feature, index) => (
            <div key={index} className="feature-card">
              <div className="feature-icon">{feature.icon}</div>
              <h4>{feature.title}</h4>
              <p>{feature.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* How to Access */}
      <div className="access-guide">
        <h2>🎯 How to Access Features</h2>
        
        <div className="access-section">
          <h3>🧭 Navigation</h3>
          <ul>
            <li><strong>Desktop:</strong> Click navigation buttons in the header</li>
            <li><strong>Mobile:</strong> Use bottom navigation bar</li>
            <li><strong>Keyboard:</strong> Tab through navigation items</li>
          </ul>
        </div>

        <div className="access-section">
          <h3>⚡ Quick Actions</h3>
          <ul>
            <li>Click the lightning bolt (⚡) in the top-right</li>
            <li>Access: Quick Trade, New Wallet, Price Alerts, Export Data</li>
            <li>Keyboard shortcut: Alt + Q (coming soon)</li>
          </ul>
        </div>

        <div className="access-section">
          <h3>🔔 Notifications</h3>
          <ul>
            <li>Click the bell icon (🔔) to view notifications</li>
            <li>Red badge shows unread count</li>
            <li>Mark all as read with one click</li>
          </ul>
        </div>

        <div className="access-section">
          <h3>👤 User Menu</h3>
          <ul>
            <li>Click your avatar in the top-right corner</li>
            <li>Access: Settings, Profile, Help, Logout</li>
            <li>View account information and preferences</li>
          </ul>
        </div>

        <div className="access-section">
          <h3>⚙️ Settings & Power Management</h3>
          <ul>
            <li><strong>Via User Menu:</strong> Avatar → Settings</li>
            <li><strong>Via Navigation:</strong> Settings tab (desktop sidebar)</li>
            <li><strong>Via Mobile:</strong> Settings in bottom navigation</li>
            <li>Configure power saving, themes, and preferences</li>
          </ul>
        </div>
      </div>

      {/* Tips & Tricks */}
      <div className="tips-section">
        <h2>💡 Pro Tips</h2>
        <div className="tips-grid">
          <div className="tip-card">
            <div className="tip-icon">⌨️</div>
            <h4>Keyboard Navigation</h4>
            <p>Use Tab and Enter to navigate through header controls without a mouse.</p>
          </div>
          <div className="tip-card">
            <div className="tip-icon">📱</div>
            <h4>Mobile Gestures</h4>
            <p>Swipe left/right on mobile to quickly switch between main sections.</p>
          </div>
          <div className="tip-card">
            <div className="tip-icon">🔋</div>
            <h4>Battery Saving</h4>
            <p>Enable Power Saver mode in Settings for extended battery life.</p>
          </div>
          <div className="tip-card">
            <div className="tip-icon">🎨</div>
            <h4>Theme Switching</h4>
            <p>Dark theme is easier on the eyes during night trading sessions.</p>
          </div>
        </div>
      </div>

      {/* Test Buttons */}
      <div className="test-section">
        <h2>🧪 Test Header Features</h2>
        <div className="test-buttons">
          <button 
            className="test-btn"
            onClick={() => showSuccess("Header navigation working perfectly!")}
          >
            Test Success Notification
          </button>
          <button 
            className="test-btn"
            onClick={() => showWarning("This is a warning notification from the header!")}
          >
            Test Warning Notification
          </button>
          <button 
            className="test-btn"
            onClick={() => showInfo("Header features are fully functional!")}
          >
            Test Info Notification
          </button>
        </div>
      </div>
    </div>
  );
};

export default HeaderDemo;